// TODO mlockard, this file is declared as side effectful but doing so is preventing most of our tree shaking
/* eslint-disable tree-shaking/no-side-effects-in-initialization */
import { ApolloClient, ApolloProvider } from "@apollo/client";
import {
  AGENT_TYPE_HEADER,
  AGENT_VERSION_HEADER,
  AgentType,
  AppFeatureFlag,
  AuthenticatedUserOrgDetails,
  DEFAULT_FLAGS,
  TRACE_ID_HEADER,
  UnAuthenticatedUserOrgDetails,
  UserOrgDetails,
  createTraceId,
} from "@hex/common";
import { History, createBrowserHistory } from "history";
import { LDClient } from "launchdarkly-js-client-sdk";
import { useLDClient } from "launchdarkly-react-client-sdk";
import MouseTrap from "mousetrap";
import React, { ReactNode, Suspense, useEffect, useMemo, useRef } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Provider as ReduxProvider } from "react-redux";
import { Redirect, Route, Router, Switch, useLocation } from "react-router-dom";
import styled from "styled-components";

import { AtomicControllerContext } from "./atomic-operations/AtomicControllerContext.js";
import { connectionManager, createClient } from "./client";
import { KernelCompletionProvider } from "./components/cell/monaco/KernelCompletionProvider.js";
import { TypeaheadCompletionProvider } from "./components/cell/monaco/TypeaheadCompletionProvider.js";
import { ElementMeasurer } from "./components/common/ElementMeasurer";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { Favicon } from "./components/common/Favicon";
import { ToasterProvider } from "./components/common/Toasts";
import { SemanticWorkbenchTypeaheadProvider } from "./components/semantic-project-workbench/monaco/publicHexSLFileYamlLanguage/SemanticWorkbenchTypeaheadCompletionProvider.js";
import { Org } from "./generated/graphqlTypes";
import { useAttribution } from "./hooks/useAttribution";
import { UrlCleaner } from "./hooks/useUrlCleaner.js";
import { ORG_ID, getUrlOrg } from "./orgs";
import {
  OrgsClientDocument,
  OrgsClientQuery,
  OrgsPickerClientDocument,
  OrgsPickerClientQuery,
} from "./orgs.generated";
import { createStore } from "./redux/store";
import AuthenticatedRouteRedirect from "./route/AuthenticatedRouteRedirect.js";
import {
  InvalidLinkRoute,
  LoggedInLinkRoute,
  WrongUserLinkRoute,
} from "./route/LinkRoutes";
import { LoginRoute } from "./route/LoginRoute";
import { MagicAuthCallbackRoute } from "./route/MagicAuthCallbackRoute";
import { MagicLinkSentRoute } from "./route/MagicLinkSentRoute";
import { Routes } from "./route/routes";
import { SignupRoute } from "./route/SignupRoute";
import { WorkspacePickerRoute } from "./route/WorkspacePickerRoute";
import { GlobalStyle } from "./theme/common/GlobalStyle";
import ThemeWrapper from "./ThemeWrapper";
import { usePerformanceStats } from "./usePerformanceStats";
import { ConnectionManagerProvider } from "./util/ConnectionManager";
import { webClientVersion } from "./util/data";
import { shouldIgnoreShortcut } from "./util/Keys";
import { getOriginOverrideUrlObject } from "./util/mcpOverrides";
import { reactLazyRetry } from "./util/reactLazyRetry";
import { RUDDER_ANALYTICS } from "./util/rudderstack.js";
import { TRACE_LOG } from "./util/TraceLog";
import { sendEvents } from "./util/trackEvent.js";
import { useGetFavicon } from "./util/useGetFavicon.js";
import { BigQueryOAuthSuccessView } from "./views/BigQueryOAuthSuccessView.js";
import { DatabricksOAuthSuccessView } from "./views/DatabricksOAuthSuccessView.js";
import { GoogleDriveOauthCompleteView } from "./views/GoogleDriveOauthCompleteView";
import { LoadingView } from "./views/LoadingView";
import { LoginSuccessView } from "./views/LoginSuccessView";
import { NotionUnfurlAuthorizeView } from "./views/NotionUnfurlAuthorizeView";
import { SnowflakeOauthSuccessView } from "./views/SnowflakeOauthSuccessView";

const urlOrg = getUrlOrg();
const appHistory = createBrowserHistory({
  basename: urlOrg != null ? `/${urlOrg}` : undefined,
});

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;

  background-color: ${({ theme }) =>
    theme.isMcpTheme ? "transparent" : theme.backgroundColor.DEFAULT};
`;

MouseTrap.prototype.stopCallback = shouldIgnoreShortcut;

const LazyOrgSelectorRoute = reactLazyRetry(
  () => import("./route/OrgSelectorRoute"),
);

const LazyHexRoutes = reactLazyRetry(
  () => import(/* webpackPrefetch: true */ "./route/HexRoutes"),
);

const LazySpellbookRoute = reactLazyRetry(
  () => import("./route/SpellbookRoute"),
);

const LazyEmbeddedAppTestPageRoute = reactLazyRetry(
  () => import("./route/EmbedAppTestRoute"),
);

const LazyMcpAppsTestRoute = reactLazyRetry(
  () => import("./route/McpAppsTestRoute"),
);

const LazyEmailRedirectedRoute = reactLazyRetry(
  () => import("./route/EmailRedirectedRoute"),
);

const LazyGitSetupRoute = reactLazyRetry(() => import("./route/GitSetupRoute"));

const PageViewWrapper: React.FunctionComponent<{ children: ReactNode }> = ({
  children,
}) => {
  // Tracks page view events via Rudderstack
  // Must be inserted between Router and Routes to track page changes
  const routerLocation = useLocation();

  useEffect(() => {
    RUDDER_ANALYTICS.page();
  }, [routerLocation]);

  return <>{children}</>;
};

const LoginRedirect: React.FunctionComponent = () => {
  const attribution = useAttribution();
  return <Redirect to={Routes.LOGIN.getUrl({ attribution })} />;
};

type AppProps = {
  /**
   * Used to override browser history for MCP apps
   */
  historyOverride?: History;
  /**
   * Used to override the Apollo client for MCP apps
   */
  clientOverride?: ApolloClient<unknown>;
};

const App: React.FunctionComponent<AppProps> = ({
  clientOverride,
  historyOverride,
}) => {
  const client = useMemo(() => {
    return clientOverride ?? createClient({});
  }, [clientOverride]);

  useEffect(() => {
    client.writeQuery<OrgsClientQuery>({
      query: OrgsClientDocument,
      data: {
        __typename: "Query",
        orgsClient: {
          __typename: "ClientOrgResponse",
          orgs: [],
          loaded: false,
        },
      },
    });

    const orgInitTraceId = createTraceId();
    const urlOverride = getOriginOverrideUrlObject();
    const base = urlOverride?.origin ?? "";

    TRACE_LOG.push({
      traceId: orgInitTraceId,
      status: "STARTING",
      name: "org init",
    });

    void fetch(`${base}/orgs`, {
      headers: {
        [TRACE_ID_HEADER]: orgInitTraceId,
        [AGENT_TYPE_HEADER]: AgentType.WEB_CLIENT,
        [AGENT_VERSION_HEADER]: webClientVersion ?? "<unknown>",
      },
    })
      .then(async (res) => {
        TRACE_LOG.push({
          traceId: orgInitTraceId,
          status: "OK",
          name: "org init",
        });
        const body: Omit<Org, "__typename">[] = await res.json();

        client.writeQuery<OrgsClientQuery>({
          query: OrgsClientDocument,
          data: {
            __typename: "Query",
            orgsClient: {
              __typename: "ClientOrgResponse",
              orgs: body.map((org) => ({ ...org, __typename: "Org" })),
              loaded: true,
            },
          },
        });
      })
      .catch((err) => {
        TRACE_LOG.push({
          traceId: orgInitTraceId,
          status: "ERROR",
          name: "org init",
        });
        throw err;
      });

    const orgPickerInitTraceId = createTraceId();

    TRACE_LOG.push({
      traceId: orgPickerInitTraceId,
      status: "STARTING",
      name: "org picker init",
    });

    void fetch(`${base}/workspace-picker-orgs`, {
      headers: {
        [TRACE_ID_HEADER]: orgPickerInitTraceId,
        [AGENT_TYPE_HEADER]: AgentType.WEB_CLIENT,
        [AGENT_VERSION_HEADER]: webClientVersion ?? "<unknown>",
      },
    })
      .then(async (res) => {
        TRACE_LOG.push({
          traceId: orgPickerInitTraceId,
          status: "OK",
          name: "org picker init",
        });
        const body = await res.json();

        // Ensure all Authed orgs match expected type
        const authenticatedUsers = body.authedOrgDetails
          .filter((data: UserOrgDetails) =>
            AuthenticatedUserOrgDetails.guard(data),
          )
          .map((data: AuthenticatedUserOrgDetails) => {
            return {
              id: data.userId,
              orgRole: data.orgRole,
              email: data.email,
              org: {
                __typename: "Org",
                id: data.id,
                displayName: data.displayName,
                // when querying for orgs we won't know when the user is requesting
                // for the SUPPORT_HEX_EMAIL so if usercount is undefined we just
                // set it to -1
                userCount: data.userCount ?? -1,
              },
              __typename: "User",
            };
          });

        // Ensure all unauthed orgs match expected type
        const unauthenticatedUsers = body.unAuthedOrgDetails
          .filter(
            (data: UserOrgDetails) =>
              UnAuthenticatedUserOrgDetails.guard(data) &&
              !AuthenticatedUserOrgDetails.guard(data),
          )
          .map((data: UserOrgDetails) => {
            return {
              __typename: "User",
              id: data.id,
              email: data.email,
              org: {
                __typename: "Org",
                id: data.id,
                displayName: data.displayName,
              },
            };
          });

        client.writeQuery<OrgsPickerClientQuery>({
          query: OrgsPickerClientDocument,
          data: {
            __typename: "Query",
            orgPickerClient: {
              __typename: "ClientOrgPickerResponse",
              loaded: true,
              authenticatedUsers,
              unauthenticatedUsers,
            },
          },
        });
      })
      .catch((err) => {
        TRACE_LOG.push({
          traceId: orgPickerInitTraceId,
          status: "ERROR",
          name: "org picker init",
        });
        throw err;
      });
  }, [client]);
  const ldClientRef = useRef<LDClient | null>(null);
  const ldClient = useLDClient();

  if (ldClient != null) {
    ldClientRef.current = ldClient;
  }

  const storeRef = useRef<ReturnType<typeof createStore>>();
  if (storeRef.current == null) {
    storeRef.current = createStore(ORG_ID, {
      client,
      getHexFlag: (flag: AppFeatureFlag) =>
        ldClientRef.current?.variation(flag) ?? DEFAULT_FLAGS[flag],
    });
  }

  const favicon = useGetFavicon({ inProject: false });

  usePerformanceStats();

  useEffect(() => {
    // ensure that we send all track event data when users closes out of session.
    // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon#sending_analytics_at_the_end_of_a_session
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        sendEvents();
      }
    });
  }, []);

  return (
    <ReduxProvider store={storeRef.current}>
      <Router history={historyOverride ?? appHistory}>
        <ErrorBoundary reloadPageOnTryAgain={true}>
          <AtomicControllerContext>
            <ApolloProvider client={client}>
              <ConnectionManagerProvider value={connectionManager}>
                <KernelCompletionProvider>
                  <TypeaheadCompletionProvider>
                    <SemanticWorkbenchTypeaheadProvider>
                      <ThemeWrapper>
                        <HelmetProvider>
                          <AppContainer>
                            <Helmet>
                              <title>Hex</title>
                            </Helmet>
                            <Favicon href={favicon} />
                            <GlobalStyle />
                            <UrlCleaner />
                            <ErrorBoundary reloadPageOnTryAgain={true}>
                              <Suspense fallback={<LoadingView />}>
                                <ToasterProvider>
                                  <ElementMeasurer />
                                  <DndProvider backend={HTML5Backend}>
                                    <PageViewWrapper>
                                      {urlOrg == null ? (
                                        <Switch>
                                          <Route path={Routes.NEW_PROJECT.path}>
                                            <AuthenticatedRouteRedirect
                                              toRoute={Routes.NEW_PROJECT}
                                            />
                                          </Route>
                                          <Route path={Routes.NEW_THREAD.path}>
                                            <AuthenticatedRouteRedirect
                                              toRoute={Routes.NEW_THREAD}
                                            />
                                          </Route>
                                          <Route
                                            path={[
                                              `${Routes.SIGNUP.path}${Routes.MAGIC_LINK_SENT.path}`,
                                              `${Routes.LOGIN.path}${Routes.MAGIC_LINK_SENT.path}`,
                                            ]}
                                          >
                                            <MagicLinkSentRoute />
                                          </Route>
                                          <Route
                                            path={
                                              Routes.MAGIC_AUTH_CALLBACK.path
                                            }
                                          >
                                            <MagicAuthCallbackRoute />
                                          </Route>
                                          <Route
                                            path={Routes.WORKSPACE_PICKER.path}
                                          >
                                            <WorkspacePickerRoute />
                                          </Route>
                                          <Route
                                            path={Routes.LINK_WRONG_USER.path}
                                          >
                                            <WrongUserLinkRoute />
                                          </Route>
                                          <Route
                                            path={Routes.LINK_LOGGED_IN.path}
                                          >
                                            <LoggedInLinkRoute />
                                          </Route>
                                          <Route
                                            path={Routes.LINK_INVALID.path}
                                          >
                                            <InvalidLinkRoute />
                                          </Route>
                                          <Route path={Routes.SIGNUP.path}>
                                            <SignupRoute />
                                          </Route>
                                          <Route path={Routes.SPELLBOOK.path}>
                                            <LazySpellbookRoute />
                                          </Route>
                                          <Route
                                            path={
                                              Routes.EMBED_APP_TEST_PAGE.path
                                            }
                                          >
                                            <LazyEmbeddedAppTestPageRoute />
                                          </Route>
                                          {process.env.NODE_ENV ===
                                            "development" && (
                                            <Route
                                              path={
                                                Routes.MCP_APPS_TEST_PAGE.path
                                              }
                                            >
                                              <LazyMcpAppsTestRoute />
                                            </Route>
                                          )}
                                          <Route
                                            path={Routes.EMAIL_REDIRECTED.path}
                                          >
                                            <LazyEmailRedirectedRoute />
                                          </Route>
                                          <Route
                                            exact={true}
                                            path={[
                                              Routes
                                                .GITHUB_PACKAGE_CONNECT_SUCCESS
                                                .path,
                                              Routes
                                                .GITHUB_PACKAGE_INSTALL_LANDING
                                                .path,
                                              Routes
                                                .GITHUB_SERVER_PACKAGE_CONNECT_SUCCESS
                                                .path,
                                              Routes
                                                .GITHUB_SERVER_PACKAGE_INSTALL_LANDING
                                                .path,
                                              Routes
                                                .GITLAB_PACKAGE_INSTALL_LANDING
                                                .path,
                                              Routes.GITHUB_SYNC_INSTALL_LANDING
                                                .path,
                                              Routes.GITHUB_SYNC_CONNECT_SUCCESS
                                                .path,
                                              Routes
                                                .GITHUB_SERVER_SYNC_INSTALL_LANDING
                                                .path,
                                              Routes
                                                .GITHUB_SERVER_SYNC_CONNECT_SUCCESS
                                                .path,
                                              Routes.GITLAB_SYNC_INSTALL_LANDING
                                                .path,
                                            ]}
                                          >
                                            <LazyGitSetupRoute />
                                          </Route>
                                          <Route
                                            exact={true}
                                            path={
                                              Routes.NOTION_UNFURL_AUTHORIZE
                                                .path
                                            }
                                          >
                                            <NotionUnfurlAuthorizeView />
                                          </Route>
                                          <Route
                                            exact={true}
                                            path={
                                              Routes.BIGQUERY_OAUTH_SUCCESS.path
                                            }
                                          >
                                            <BigQueryOAuthSuccessView />
                                          </Route>
                                          <Route
                                            exact={true}
                                            path={
                                              Routes.SNOWFLAKE_OAUTH_SUCCESS
                                                .path
                                            }
                                          >
                                            <SnowflakeOauthSuccessView />
                                          </Route>
                                          <Route
                                            exact={true}
                                            path={
                                              Routes.DATABRICKS_OAUTH_SUCCESS
                                                .path
                                            }
                                          >
                                            <DatabricksOAuthSuccessView />
                                          </Route>
                                          <Route
                                            exact={true}
                                            path={
                                              Routes.GOOGLE_DRIVE_OAUTH_COMPLETE
                                                .path
                                            }
                                          >
                                            <GoogleDriveOauthCompleteView />
                                          </Route>
                                          <Route
                                            path={Routes.ORG_SELECTOR.path}
                                          >
                                            <LazyOrgSelectorRoute />
                                          </Route>
                                          <Route path={Routes.LOGIN.path}>
                                            <LoginRoute />
                                          </Route>
                                          <Route
                                            exact={true}
                                            path={Routes.LOGIN_SUCCESS.path}
                                          >
                                            <LoginSuccessView />
                                          </Route>
                                          <Route path="/">
                                            <LoginRedirect />
                                          </Route>
                                        </Switch>
                                      ) : (
                                        <LazyHexRoutes />
                                      )}
                                    </PageViewWrapper>
                                  </DndProvider>
                                </ToasterProvider>
                              </Suspense>
                            </ErrorBoundary>
                          </AppContainer>
                        </HelmetProvider>
                      </ThemeWrapper>
                    </SemanticWorkbenchTypeaheadProvider>
                  </TypeaheadCompletionProvider>
                </KernelCompletionProvider>
              </ConnectionManagerProvider>
            </ApolloProvider>
          </AtomicControllerContext>
        </ErrorBoundary>
      </Router>
    </ReduxProvider>
  );
};

export default App;
